import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  X,
} from 'lucide-react'
import {
  format,
  addMonths,
  subMonths,
  parseISO,
  startOfMonth,
  isSameMonth,
} from 'date-fns'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  ReportParams,
  GroupByOption,
  RevenueStreamId,
  DbUnitType,
} from '@/lib/reports/space-yield/types'
import { formatStatus } from '@/lib/reports/space-yield/compute'
import { SpaceTypeToggles } from './SpaceTypeToggles'
import { RevenueStreamToggles } from './RevenueStreamToggles'
import { ColumnPicker } from './ColumnPicker'

interface SpaceYieldControlBarProps {
  params: ReportParams
  onParamsChange: (updates: Partial<ReportParams>) => void
  onClearStatus: () => void
  onExport: () => void
  unitTypeCounts: Record<string, number>
  onColumnsChange: (columns: string[]) => void
  hasFuelData?: boolean
}

export function SpaceYieldControlBar({
  params,
  onParamsChange,
  onClearStatus,
  onExport,
  unitTypeCounts,
  onColumnsChange,
  hasFuelData,
}: SpaceYieldControlBarProps) {
  const monthDate = parseISO(`${params.period}-01`)
  const now = new Date()
  const isMtd = isSameMonth(monthDate, now)
  const isCurrentOrFuture = monthDate >= startOfMonth(now)

  const handleTypesChange = (types: DbUnitType[]) => {
    onParamsChange({ types })
  }

  const handleStreamsChange = (streams: RevenueStreamId[]) => {
    onParamsChange({ streams })
  }

  const prevMonth = () => {
    const prev = format(subMonths(monthDate, 1), 'yyyy-MM')
    onParamsChange({ period: prev })
  }

  const nextMonth = () => {
    if (isCurrentOrFuture) return // Can't go to future
    const next = format(addMonths(monthDate, 1), 'yyyy-MM')
    onParamsChange({ period: next })
  }

  const periodDisplay = format(monthDate, 'MMM yyyy')

  return (
    <div className="border-b bg-white p-4 space-y-3 md:space-y-0 md:flex md:items-center md:justify-between sticky top-0 z-10 shadow-sm">
      {/* Left Group */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Period Selector */}
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-500" />
          <div className="flex items-center bg-slate-100 rounded-md p-1">
            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-sm" onClick={prevMonth}>
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <span className="text-sm font-medium px-2 min-w-[100px] text-center">
              {periodDisplay}
              {isMtd && (
                <Badge variant="outline" className="ml-1 text-[10px] py-0 px-1 text-amber-600 border-amber-300">
                  MTD
                </Badge>
              )}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-sm"
              onClick={nextMonth}
              disabled={isCurrentOrFuture}
            >
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Separator */}
        <div className="h-4 w-px bg-slate-200 hidden md:block" />

        {/* Space Type Toggles */}
        <SpaceTypeToggles
          unitTypeCounts={unitTypeCounts}
          types={params.types}
          onTypesChange={handleTypesChange}
        />

        {/* Separator */}
        <div className="h-4 w-px bg-slate-200 hidden md:block" />

        {/* Revenue Stream Toggles (fuel hidden for MVP) */}
        <RevenueStreamToggles
          streams={params.streams}
          onStreamsChange={handleStreamsChange}
          hasFuelData={false}
        />

        {/* Quick Filter Chip (from KPI click) */}
        {params.status && (
          <>
            <div className="h-4 w-px bg-slate-200 hidden md:block" />
            <Badge
              variant="outline"
              className="cursor-pointer border-blue-300 text-blue-700 hover:bg-blue-50 gap-1"
              onClick={onClearStatus}
            >
              {formatStatus(params.status)}
              <X className="w-3 h-3" />
            </Badge>
          </>
        )}
      </div>

      {/* Right Group */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Group By */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 font-medium">Group:</span>
          <Select
            value={params.group_by}
            onValueChange={(v) => onParamsChange({ group_by: v as GroupByOption })}
          >
            <SelectTrigger className="w-[130px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="building">Building</SelectItem>
              <SelectItem value="type">Unit Type</SelectItem>
              <SelectItem value="status">Status</SelectItem>
              <SelectItem value="segment">Rev. Segment</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <ColumnPicker
          visibleColumns={params.columns}
          onColumnsChange={onColumnsChange}
          hasFuelData={hasFuelData}
        />

        <Button variant="outline" size="sm" className="h-9" onClick={onExport}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>
    </div>
  )
}
